import { useCallback, useEffect, useRef, useState } from 'react';

import { UserPrivateStationProps } from '../types/responses/UsersPrivateStation';
import { searchUsers } from './users';

function useUserSearch(
  chargeBoxPK: number | null,
  boundUsers: UserPrivateStationProps[],
  { minChars = 3, debounceMs = 300 }: { minChars?: number; debounceMs?: number } = {},
) {
  const [searchResults, setSearchResults] = useState<UserPrivateStationProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSearch = useRef<string>('');

  const options: UserPrivateStationProps[] = searchResults.length > 0 ? searchResults : boundUsers;

  const onSearch = useCallback(
    (text: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      if (!text || text.length < minChars) {
        setSearchResults([]);
        return;
      }

      debounceTimer.current = setTimeout(async () => {
        currentSearch.current = text;
        setIsLoading(true);
        try {
          const result = await searchUsers(chargeBoxPK, text);
          if (currentSearch.current === text) {
            setSearchResults(result?.userPrivateStationList ?? []);
          }
        } catch {
          if (currentSearch.current === text) {
            setSearchResults([]);
          }
        } finally {
          if (currentSearch.current === text) {
            setIsLoading(false);
          }
        }
      }, debounceMs);
    },
    [chargeBoxPK, minChars, debounceMs],
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return { options, isLoading, onSearch };
}

export default useUserSearch;
